/**
 * @kantera/api-client — shared HTTP client for web + mobile.
 *
 * Provides:
 *   - typed `apiGet` / `apiPost` / `apiPatch` / `apiDelete` functions
 *   - `ApiError` class carrying HTTP status + message
 *   - optional 503 retry with backoff
 *   - AbortSignal support for caller-managed cancellation
 *   - Bearer-token auth header construction
 *
 * The 4 functions match the existing mobile `apps/mobile/src/api/client.ts`
 * surface so mobile can switch to this package by changing one import line.
 *
 * Web's `apps/web/src/hooks/useApi.ts` should wrap these (rather than
 * duplicating the fetch/retry/error logic) — request shaping moves here,
 * React state stays in the hook. Migration of the existing call sites is
 * a follow-up PR; this package ships the contract first.
 *
 * KI-038 closure plan:
 *   PR 1 (this) — package skeleton + imperative core + 0-impact ship
 *   PR 2        — migrate `apps/mobile/src/api/client.ts` to re-export
 *   PR 3        — migrate `apps/web/src/hooks/useApi.ts` to wrap this
 *   PR 4        — fold useMutation onto the same primitives
 */
// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------
/**
 * Thrown by every fetcher in this module on non-2xx responses. Preserves
 * the HTTP status so callers can branch on `err.status === 401` etc., and the
 * API's machine `code` when the body carried one — two refusals can share a
 * status (410 INVITE_EXPIRED vs 410 INVITE_ALREADY_ACCEPTED) and mean different
 * things to show someone.
 *
 * `retryAfter` is set only for the API rate LIMITER's 429 (`code:
 * 'RATE_LIMIT_EXCEEDED'`): the seconds until it resets, or null when it did not
 * say. The public invite pages read it (KAN-139) — they have no app-shell banner,
 * and React Native has no window for RATE_LIMITED_EVENT — so a rate-limited
 * lookup says "try again in N s" instead of "this link is no longer valid".
 */
export class ApiError extends Error {
    status;
    code;
    retryAfter;
    constructor(status, message, code, retryAfter) {
        super(message);
        this.status = status;
        this.name = 'ApiError';
        this.code = code ?? null;
        this.retryAfter = retryAfter ?? null;
    }
}
const DEFAULT_MAX_RETRIES = 2;
const DEFAULT_RETRY_DELAY_MS = 1000;
// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------
/**
 * Resolves after `ms` milliseconds. Rejects with `AbortError` if the
 * signal is aborted before the timer fires.
 */
function delay(ms, signal) {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(resolve, ms);
        if (signal) {
            signal.addEventListener('abort', () => {
                clearTimeout(timer);
                reject(new DOMException('Aborted', 'AbortError'));
            });
        }
    });
}
/**
 * Content-Type is set only when the request carries a JSON body (KAN-59).
 *
 * It used to go on every request. Fastify 5 reads `application/json` as a
 * promise of a JSON body, finds none on a DELETE, and answers 400
 * FST_ERR_CTP_EMPTY_JSON_BODY before auth runs, so every delete in the product
 * failed. Through the API's errorHandler that arrives as
 *   {"code":"FASTIFY_ERROR","message":"Body cannot be empty when content-type is set to 'application/json'"}
 * Every body this module sends is a JSON.stringify result, so "is a
 * string" means "is JSON". JSON.stringify(undefined) is undefined, so a POST
 * with no body gets no header either. Anything that is not a string (FormData,
 * Blob) must not get a hand-set Content-Type: the runtime sets the multipart
 * boundary itself.
 */
function buildHeaders(token, body) {
    const headers = {};
    if (typeof body === 'string')
        headers['Content-Type'] = 'application/json';
    if (token)
        headers['Authorization'] = `Bearer ${token}`;
    return headers;
}
/**
 * A path segment that reads `undefined` or `null` is never intentional.
 *
 * It is a template literal that interpolated a value the caller thought it had:
 * `/api/data-export/users/${user.userId}` when userId was missing. The request
 * fires, the server answers 400 or 404, and the UI shows a generic failure — so
 * the symptom points at the API while the defect is a missing guard in the
 * caller. The real-click crawler found exactly that on the consent page.
 *
 * Matched on whole SEGMENTS, not as a substring: `/api/undefined-thing` and a
 * query value of `undefined` are the caller's business, and a resource whose id
 * genuinely is the word "undefined" would be a different problem entirely.
 */
const PLACEHOLDER_SEGMENT = /\/(undefined|null|NaN)(?=\/|\?|#|$)/;
/**
 * The same defect with the value coalesced away: `/api/parents/${parentId ?? ''}/children`
 * sends `/api/parents//children` (KAN-2, /my-children's PIN form). Checked on the PATH only —
 * a `//` in a query value (`?next=https://…`) or a fragment is data, not an empty segment.
 * A trailing slash is deliberately NOT treated as empty: that is a routing question, not a
 * missing value.
 */
const EMPTY_SEGMENT = /\/\//;
export class InvalidRequestPathError extends Error {
    path;
    constructor(path) {
        super(`Refusing to request "${path}" — a path segment is empty, "undefined", "null" or "NaN". ` +
            'Something was interpolated into the URL before it was known. Guard the caller ' +
            'rather than letting the server answer 400.');
        this.path = path;
        this.name = 'InvalidRequestPathError';
    }
}
function buildUrl(baseUrl, path) {
    const trimmedBase = baseUrl.replace(/\/$/, '');
    const prefixedPath = path.startsWith('/') ? path : `/${path}`;
    // Thrown, not logged. A warning would leave the broken request in flight and
    // the useless 400 still reaching the user; failing here puts the message in
    // front of whoever can fix it.
    const withoutFragment = prefixedPath.split('#')[0] ?? prefixedPath;
    const pathOnly = withoutFragment.split('?')[0] ?? withoutFragment;
    if (PLACEHOLDER_SEGMENT.test(withoutFragment) || EMPTY_SEGMENT.test(pathOnly)) {
        throw new InvalidRequestPathError(prefixedPath);
    }
    return `${trimmedBase}${prefixedPath}`;
}
/**
 * Generic per-status fallback phrases for when the response body is empty.
 * Avoids the raw `""` / `res.statusText` landing in the UI as `ApiError.message`.
 */
function genericMessageForStatus(status) {
    if (status === 400)
        return 'Bad request';
    if (status === 401)
        return 'Unauthorized — please sign in again';
    if (status === 403)
        return 'You do not have permission to access this resource';
    if (status === 404)
        return 'Resource not found';
    if (status === 409)
        return 'Conflict — the resource already exists';
    if (status === 422)
        return 'Validation error';
    if (status === 429)
        return 'Too many requests — please wait and try again';
    if (status >= 500)
        return 'Server error — please try again later';
    return `Request failed (${status})`;
}
/**
 * Reads an error response body. `null` means the body could not be read at all,
 * which is distinct from an empty body. A Response body can be consumed only
 * once, so an error path reads it here, once, and passes the text on (the 429
 * path needs it for both the rate-limit event and the ApiError).
 */
async function readErrorBody(res) {
    try {
        return await res.text();
    }
    catch {
        return null;
    }
}
/**
 * The ApiError for a non-2xx response whose body has already been read: its message,
 * its code when present, and — for the limiter's 429 only — the seconds to wait.
 */
function apiErrorFromBody(res, text, retryAfter = null) {
    if (text === null) {
        return new ApiError(res.status, res.statusText || genericMessageForStatus(res.status));
    }
    let code = null;
    try {
        const parsed = JSON.parse(text);
        if (typeof parsed.code === 'string')
            code = parsed.code;
    }
    catch {
        // Not JSON — no code; readErrorMessage below decides the message.
    }
    return new ApiError(res.status, readErrorMessage(res.status, text), code, retryAfter);
}
/** Build the ApiError for a non-2xx response: its message and, when present, its code. */
async function toApiError(res) {
    return apiErrorFromBody(res, await readErrorBody(res));
}
/** The human-readable message for an error body (JSON `message`/`error` plus field reasons, or plain text). */
function readErrorMessage(status, text) {
    if (!text.trim())
        return genericMessageForStatus(status);
    try {
        const parsed = JSON.parse(text);
        const msg = (parsed.message ?? parsed.error);
        const base = msg && typeof msg === 'string' ? msg : genericMessageForStatus(status);
        // Append the field-level reasons when the API sent them. The server puts the
        // only actionable text in details.issues[].message (see the API's
        // middleware/validation.ts buildDetails); reading just `message` collapsed
        // every rejected write to "Invalid request body", which pages then replaced
        // with their own generic toast. That is how a 400 on one numeric field
        // reached the user as "check connection".
        const details = parsed.details;
        const issues = Array.isArray(details?.issues) ? details.issues : undefined;
        if (issues?.length) {
            const reasons = issues
                .slice(0, 3)
                .map((raw) => {
                const issue = raw;
                const field = typeof issue.field === 'string' ? issue.field : '';
                const reason = typeof issue.message === 'string' ? issue.message : '';
                if (!reason)
                    return field;
                return field ? `${field}: ${reason}` : reason;
            })
                .filter(Boolean);
            if (reasons.length) {
                const more = issues.length > reasons.length ? ` (+${issues.length - reasons.length} more)` : '';
                return `${base} — ${reasons.join('; ')}${more}`;
            }
        }
        return base;
    }
    catch {
        // Body was not JSON — return raw text only if it looks human-readable
        // (i.e. does not start with "{" or "["), otherwise fall back to the generic phrase.
        const trimmed = text.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            return genericMessageForStatus(status);
        }
        return trimmed || genericMessageForStatus(status);
    }
}
// ---------------------------------------------------------------------------
// Rate limiting (429)
// ---------------------------------------------------------------------------
/**
 * Fired on `window` when the API's rate LIMITER answers 429.
 *
 * KAN-139: the API limits each user per minute, and this client deliberately
 * does not retry a 429 (retrying a rate limit only deepens it). A page that
 * fans out one request per row then loses some of them and renders holes with
 * no explanation. Every request passes through `request()` below, so it
 * announces the 429 here and the web app shell (SystemStatusBanner) says what
 * happened and when to try again. The caller still gets its ApiError as before.
 *
 * Only the limiter's 429, identified by RATE_LIMIT_CODE in the body. The API
 * also answers 429 for business quotas (the hourly AI-report quota, the video
 * storage quota) that neither reset in a minute nor go away on reload, and a
 * 429 with no JSON body cannot be told apart from those — none of them fire.
 */
export const RATE_LIMITED_EVENT = 'kantera:rate-limited';
/** The `code` the API's limiter puts in its 429 body (apps/api middleware/errors.ts). */
const RATE_LIMIT_CODE = 'RATE_LIMIT_EXCEEDED';
/**
 * The limiter's 429 body, or `null` when this 429 is something else — a business
 * quota, or a body that is empty or not JSON.
 */
function rateLimiterBody(text) {
    if (!text)
        return null;
    try {
        const parsed = JSON.parse(text);
        return parsed !== null && typeof parsed === 'object' && parsed.code === RATE_LIMIT_CODE
            ? parsed
            : null;
    }
    catch {
        return null;
    }
}
/**
 * Seconds to wait, preferring the body's `retryAfter` (the API's own number for
 * this limit) over the `Retry-After` header, which may be either delta-seconds
 * or an HTTP date.
 */
function retryAfterSeconds(body, header) {
    const fromBody = body.retryAfter;
    if (typeof fromBody === 'number' && Number.isFinite(fromBody) && fromBody >= 0) {
        return Math.ceil(fromBody);
    }
    const raw = header?.trim();
    if (!raw)
        return null;
    if (/^\d+$/.test(raw))
        return Number(raw);
    const at = Date.parse(raw);
    if (Number.isNaN(at))
        return null;
    return Math.max(0, Math.ceil((at - Date.now()) / 1000));
}
/**
 * Dispatch RATE_LIMITED_EVENT, or do nothing when there is no browser to tell.
 *
 * This package also runs in Node (tests, scripts) and on React Native, where
 * `window` is the global object with no `dispatchEvent`, and older Hermes has
 * no `CustomEvent`. None of those may turn a 429 into a TypeError.
 */
function announceRateLimited(detail) {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function')
        return;
    if (typeof CustomEvent !== 'function')
        return;
    try {
        window.dispatchEvent(new CustomEvent(RATE_LIMITED_EVENT, { detail }));
    }
    catch {
        // Telling the page is best-effort; the ApiError the caller gets is the contract.
    }
}
/** 30 seconds. Long enough for a slow report, short enough to be an answer. */
const DEFAULT_TIMEOUT_MS = 30_000;
/**
 * Thrown when a request runs out of time. Distinct from a caller's own abort,
 * because "you cancelled this" and "the server never answered" are different
 * things to show someone.
 */
export class ApiTimeoutError extends Error {
    path;
    timeoutMs;
    constructor(path, timeoutMs) {
        super(`The server did not respond within ${Math.round(timeoutMs / 1000)} seconds. ` +
            'Nothing was changed — please try again.');
        this.path = path;
        this.timeoutMs = timeoutMs;
        this.name = 'ApiTimeoutError';
    }
}
/**
 * Combine the caller's signal with a timeout.
 *
 * Returns the signal to use plus a `cleanup` that MUST run, or a long-lived
 * page accumulates one live timer per request.
 */
function withTimeout(signal, timeoutMs) {
    if (timeoutMs <= 0)
        return { signal, timedOut: () => false, cleanup: () => { } };
    const controller = new AbortController();
    let fired = false;
    const timer = setTimeout(() => {
        fired = true;
        controller.abort();
    }, timeoutMs);
    // The caller's own abort (unmount, dependency change) still wins.
    if (signal) {
        if (signal.aborted)
            controller.abort();
        else
            signal.addEventListener('abort', () => controller.abort(), { once: true });
    }
    return {
        signal: controller.signal,
        timedOut: () => fired,
        cleanup: () => clearTimeout(timer),
    };
}
/**
 * Core request function — handles URL building, headers, retries, error mapping.
 * The verb wrappers below specialize this for the common HTTP methods.
 */
async function request(path, init, options) {
    const url = buildUrl(options.baseUrl, path);
    // Merged through Headers because header names are case-insensitive: a
    // caller's `content-type` (any spelling) REPLACES the default. Spreading plain
    // objects kept both keys, and fetch sent them joined ("application/json,
    // text/plain").
    const headers = new Headers(buildHeaders(options.token, init.body));
    for (const extra of [options.headers, init.headers]) {
        new Headers(extra).forEach((value, name) => headers.set(name, value));
    }
    const maxRetries = options.retry503 ? (options.maxRetries ?? DEFAULT_MAX_RETRIES) : 0;
    const retryDelay = options.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS;
    let lastError = null;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        if (attempt > 0) {
            await delay(retryDelay, options.signal);
        }
        const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
        const bounded = withTimeout(options.signal, timeoutMs);
        let res;
        try {
            res = await fetch(url, { ...init, headers, signal: bounded.signal });
        }
        catch (err) {
            // An abort that came from OUR timer is a timeout; one that came from the
            // caller is a cancellation and must keep propagating as such, so the
            // hook's `if (aborted) return` still suppresses it.
            if (bounded.timedOut())
                throw new ApiTimeoutError(path, timeoutMs);
            throw err;
        }
        finally {
            bounded.cleanup();
        }
        if (res.status === 503 && attempt < maxRetries) {
            lastError = await toApiError(res);
            continue;
        }
        if (res.status === 429) {
            // Never retried. The body is read once: the message, and whether this is the
            // limiter at all, both come from it.
            const text = await readErrorBody(res);
            const limiter = rateLimiterBody(text);
            // `?.`: test doubles for fetch often return a bare { ok, status, text } with no headers.
            const retryAfter = limiter
                ? retryAfterSeconds(limiter, res.headers?.get('retry-after') ?? null)
                : null;
            if (limiter)
                announceRateLimited({ retryAfter, path });
            throw apiErrorFromBody(res, text, retryAfter);
        }
        if (!res.ok) {
            throw await toApiError(res);
        }
        // 204 No Content path — common for DELETE
        if (res.status === 204)
            return undefined;
        const text = await res.text();
        if (!text)
            return undefined;
        return JSON.parse(text);
    }
    throw lastError ?? new ApiError(503, 'Service unavailable after retries');
}
// ---------------------------------------------------------------------------
// Verb wrappers — match the existing mobile client.ts shape
// ---------------------------------------------------------------------------
export async function apiGet(path, options) {
    return request(path, { method: 'GET' }, options);
}
export async function apiPost(path, body, options) {
    return request(path, { method: 'POST', body: JSON.stringify(body) }, options);
}
export async function apiPatch(path, body, options) {
    return request(path, { method: 'PATCH', body: JSON.stringify(body) }, options);
}
export async function apiPut(path, body, options) {
    return request(path, { method: 'PUT', body: JSON.stringify(body) }, options);
}
export async function apiDelete(path, options) {
    return request(path, { method: 'DELETE' }, options);
}
//# sourceMappingURL=index.js.map